import React from "react"
import { Box, ButtonBase, Typography } from "@mui/material"
import { ViraIcon } from "../atoms/ViraIcon"
import { colors, type ViraThemeMode } from "../theme/tokens"
import { resolveViraPalette } from "../theme/resolveViraPalette"

export type ThemeSwitcherSize = "sm" | "md"

export type ThemeSwitcherProps = {
  /** Active color mode. Parent owns state and passes it to `ViraThemeProvider`. */
  mode: ViraThemeMode
  onChange: (mode: ViraThemeMode) => void
  /** Control height / icon scale. Defaults to `md`. */
  size?: ThemeSwitcherSize
  /** Show “Light” / “Dark” text beside icons. Defaults to icons-only. */
  showLabels?: boolean
  disabled?: boolean
  /** Accessible name for the control group. */
  "aria-label"?: string
}

const SIZE = {
  sm: { height: 32, icon: 16, fontSize: 12, px: 1.25, gap: 0.5 },
  md: { height: 40, icon: 18, fontSize: 13, px: 1.5, gap: 0.75 },
} as const

const OPTIONS: {
  value: ViraThemeMode
  label: string
  icon: "sun" | "moon"
}[] = [
  { value: "light", label: "Light", icon: "sun" },
  { value: "dark", label: "Dark", icon: "moon" },
]

/**
 * Presentational light/dark toggle. Pair with `ViraThemeProvider` + parent
 * mode state (see `useViraColorMode`).
 */
export function ThemeSwitcher({
  mode,
  onChange,
  size = "md",
  showLabels = false,
  disabled = false,
  "aria-label": ariaLabel = "Color theme",
}: ThemeSwitcherProps) {
  const s = SIZE[size]

  return (
    <Box
      role="group"
      aria-label={ariaLabel}
      sx={(theme) => ({
        display: "inline-flex",
        height: s.height,
        borderRadius: 999,
        overflow: "hidden",
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? "none" : "auto",
        backgroundColor:
          theme.palette.mode === "dark"
            ? resolveViraPalette(theme).lavender
            : "rgba(193,206,243,0.4)",
        border: `1px solid ${
          theme.palette.mode === "dark"
            ? resolveViraPalette(theme).lavenderBorder
            : resolveViraPalette(theme).border
        }`,
      })}
    >
      {OPTIONS.map((option, index) => {
        const selected = option.value === mode
        const isFirst = index === 0
        const isLast = index === OPTIONS.length - 1
        return (
          <ButtonBase
            key={option.value}
            onClick={() => {
              if (!selected) onChange(option.value)
            }}
            aria-label={option.label}
            aria-pressed={selected}
            disabled={disabled}
            sx={(theme) => ({
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: s.gap,
              px: s.px,
              minWidth: showLabels ? undefined : s.height + 8,
              backgroundColor: selected ? colors.primary.ice : "transparent",
              borderRadius: isFirst
                ? "999px 0 0 999px"
                : isLast
                  ? "0 999px 999px 0"
                  : 0,
              transition: theme.transitions.create(["background-color"], {
                duration: theme.transitions.duration.shorter,
              }),
              "&:hover": {
                backgroundColor: selected
                  ? colors.primary.ice
                  : theme.palette.mode === "dark"
                    ? resolveViraPalette(theme).navActiveBg
                    : "rgba(193,206,243,0.55)",
              },
            })}
          >
            <ViraIcon
              name={option.icon}
              tone={selected ? "ink" : "subtle"}
              sx={{
                fontSize: s.icon,
                color: selected
                  ? colors.primary.midnight
                  : undefined,
              }}
            />
            {showLabels ? (
              <Typography
                component="span"
                sx={(theme) => ({
                  fontFamily: '"Lato", sans-serif',
                  fontWeight: 700,
                  fontSize: s.fontSize,
                  lineHeight: 1,
                  color: selected
                    ? colors.primary.midnight
                    : theme.palette.mode === "dark"
                      ? resolveViraPalette(theme).muted
                      : colors.primary.midnight,
                })}
              >
                {option.label}
              </Typography>
            ) : null}
          </ButtonBase>
        )
      })}
    </Box>
  )
}
